import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpinoffPlatformOrgContractService } from '@sbe-spinoff-client/spinoff-platform';
import { environment } from '../../environments/environment';
import type { OrgDto } from '@sbe-spinoff-client/sbe-conduct-api';

const STORAGE_KEY = 'conduct.activeOrgId';

/** Forget the school on sign-out (`environment.forgetSchoolOnSignOut`; local dev keeps it). */
export const FORGET_SCHOOL_ON_SIGN_OUT = new InjectionToken<boolean>(
  'FORGET_SCHOOL_ON_SIGN_OUT',
  { factory: () => environment.forgetSchoolOnSignOut },
);

/**
 * 지금 보고 있는 학교. 학교 화면은 `/orgs/:orgId/...` 이고, 라우트 가드가 URL 의 학교를 `select()` 로 넣는다.
 * 브라우저에도 기억해, 학교가 없는 주소(`/dashboard`, 로그인 복귀 기본값)가 마지막 학교를 열 때 쓴다.
 * conduct 는 학교를 학교종이 「진입」으로만 안다 — 진입 응답의 `activeOrgId` 가 `/orgs/:orgId` 로 온다.
 * `GET /orgs` 는 로컬 구성원 행으로 학교를 찾는데 conduct 는 구성원을 저장하지 않아 늘 비어 있어 부르지 않는다.
 * 학교 단위 서비스는 여기서 `orgId`를 받는다. 학교 이름·내 역할은 `GET /orgs/:orgId`(`refresh`)로 읽는다.
 */
@Injectable({ providedIn: 'root' })
export class ConductActiveOrgService {
  private readonly api = inject(SpinoffPlatformOrgContractService);
  private readonly forgetOnSignOut = inject(FORGET_SCHOOL_ON_SIGN_OUT);
  private readonly selectedId = signal(this.readSelectedId());
  private readonly loadedSchool = signal<Pick<
    OrgDto,
    'name' | 'myRole'
  > | null>(null);
  private readonly fetching = signal(false);

  readonly orgId = this.selectedId.asReadonly();
  // Links inside the open school start here, e.g. `[schoolPath(), 'entries', 'grant']`.
  readonly schoolPath = computed(() => `/orgs/${this.selectedId()}`);
  // Empty until `refresh` answers, or when it fails.
  readonly school = this.loadedSchool.asReadonly();
  // While true, the screen waits for the name instead of flashing the fallback text.
  readonly loading = this.fetching.asReadonly();

  // Only the latest refresh lands — an older answer (another school) is dropped.
  private latestRefresh = 0;

  select(orgId: string) {
    this.selectedId.set(orgId);
    this.saveSelectedId(orgId);
  }

  async refresh(orgId: string) {
    const call = ++this.latestRefresh;

    this.loadedSchool.set(null);
    this.fetching.set(true);

    try {
      const { name, myRole } = await firstValueFrom(
        this.api.spinoffOrgsControllerGetOrg(orgId),
      );

      if (call === this.latestRefresh) this.loadedSchool.set({ name, myRole });
    } catch (error) {
      console.warn('[conduct] could not load the selected school', error);
    } finally {
      if (call === this.latestRefresh) this.fetching.set(false);
    }
  }

  // On sign-out: the remembered school belongs to the person who chose it.
  clear() {
    this.latestRefresh++;
    this.loadedSchool.set(null);
    this.fetching.set(false);

    if (!this.forgetOnSignOut) return;

    this.selectedId.set(null);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[conduct] could not forget the selected school', error);
    }
  }

  private readSelectedId() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[conduct] could not read the selected school', error);

      return null;
    }
  }

  private saveSelectedId(orgId: string) {
    try {
      localStorage.setItem(STORAGE_KEY, orgId);
    } catch (error) {
      console.warn('[conduct] could not save the selected school', error);
    }
  }
}
